import React, { useCallback, useEffect, useState } from 'react'
import {
  Alert, Box, Button, Card, CardContent, Chip, CircularProgress, Dialog,
  DialogActions, DialogContent, DialogTitle, Typography,
} from '@mui/material'
import ForumIcon        from '@mui/icons-material/Forum'
import AutoFixHighIcon  from '@mui/icons-material/AutoFixHigh'
import { supabase } from '../../supabaseClient'
import { useAuth }  from '../../auth/AuthContext'
import PolishComposer from '../messaging/PolishComposer'

// v1: only the triage inbox owner sees the parent-messages queue.
const TRIAGE_EMAIL = 'dr.li@ardentacademy.com'

interface OpenMessage {
  id:           string
  student_id:   string
  body:         string
  created_at:   string
  student_name: string
  parent_name:  string
}

function fmt(iso: string) {
  return new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
}

export default function ParentMessagesPanel() {
  const { profile } = useAuth()
  const isTriager = profile?.email?.toLowerCase() === TRIAGE_EMAIL
  const [items,   setItems]   = useState<OpenMessage[]>([])
  const [loading, setLoading] = useState(true)
  const [active,  setActive]  = useState<OpenMessage | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    const { data: rows } = await supabase
      .from('parent_message')
      .select('id, student_id, parent_user_id, body, created_at')
      .eq('direction', 'parent_to_staff')
      .eq('status', 'open')
      .order('created_at', { ascending: false })

    const msgs = rows ?? []
    const studentIds = Array.from(new Set(msgs.map(m => m.student_id)))
    const parentIds  = Array.from(new Set(msgs.map(m => m.parent_user_id)))

    const [{ data: sps }, { data: parents }] = await Promise.all([
      studentIds.length
        ? supabase.from('student_profile').select('student_id, user_id').in('student_id', studentIds)
        : Promise.resolve({ data: [] as { student_id: string; user_id: string }[] }),
      parentIds.length
        ? supabase.from('users').select('user_id, first_name, last_name').in('user_id', parentIds)
        : Promise.resolve({ data: [] as { user_id: string; first_name: string; last_name: string }[] }),
    ])
    const spMap  = Object.fromEntries((sps ?? []).map(s => [s.student_id, s.user_id]))
    const stUserIds = Array.from(new Set(Object.values(spMap)))
    const { data: stUsers } = stUserIds.length
      ? await supabase.from('users').select('user_id, first_name, last_name').in('user_id', stUserIds)
      : { data: [] as { user_id: string; first_name: string; last_name: string }[] }
    const nameOf = (uid?: string) => {
      const u = (stUsers ?? []).concat(parents ?? []).find(x => x.user_id === uid)
      return u ? `${u.first_name ?? ''} ${u.last_name ?? ''}`.trim() : ''
    }

    setItems(msgs.map(m => ({
      id:           m.id,
      student_id:   m.student_id,
      body:         m.body,
      created_at:   m.created_at,
      student_name: nameOf(spMap[m.student_id]) || 'Student',
      parent_name:  nameOf(m.parent_user_id) || 'Parent',
    })))
    setLoading(false)
  }, [])

  useEffect(() => { if (isTriager) load() }, [isTriager, load])

  if (!isTriager) return null
  // Don't take up space when the queue is empty — the panel appears only when
  // there's actually a parent message waiting.
  if (items.length === 0) return null

  return (
    <Card variant="outlined" sx={{ mb: 3 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
          <ForumIcon color="info" fontSize="small" />
          <Typography variant="subtitle1" sx={{ fontWeight: 700, flex: 1 }}>Parent Messages</Typography>
          {items.length > 0 && <Chip size="small" color="info" label={items.length} />}
        </Box>

        {loading ? (
          <Box sx={{ py: 2, textAlign: 'center' }}><CircularProgress size={20} /></Box>
        ) : items.length === 0 ? (
          <Typography variant="body2" color="text.secondary">No parent messages waiting.</Typography>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {items.map(m => (
              <Box
                key={m.id}
                sx={{
                  display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2,
                  border: '1px solid', borderColor: 'divider', borderRadius: 1.5, px: 1.75, py: 1.25,
                }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {m.parent_name} · <span style={{ fontWeight: 400 }}>re {m.student_name}</span>
                  </Typography>
                  <Typography variant="body2" color="text.secondary" noWrap sx={{ maxWidth: 520 }}>
                    {m.body}
                  </Typography>
                  <Typography variant="caption" color="text.disabled">{fmt(m.created_at)}</Typography>
                </Box>
                <Button size="small" variant="contained" onClick={() => setActive(m)} sx={{ flexShrink: 0 }}>
                  Reply
                </Button>
              </Box>
            ))}
          </Box>
        )}
      </CardContent>

      <ReplyDialog
        message={active}
        onClose={() => setActive(null)}
        onSent={() => { setActive(null); load() }}
      />
    </Card>
  )
}

function ReplyDialog({
  message, onClose, onSent,
}: {
  message: OpenMessage | null
  onClose: () => void
  onSent:  () => void
}) {
  const [reply,    setReply]    = useState('')
  const [drafting, setDrafting] = useState(false)
  const [error,    setError]    = useState<string | null>(null)

  useEffect(() => { setReply(''); setError(null) }, [message?.id])

  async function draft() {
    if (!message || drafting) return
    setDrafting(true); setError(null)
    try {
      const { data, error: invokeErr } = await supabase.functions.invoke('draft-parent-reply', {
        body: { message_id: message.id },
      })
      if (invokeErr) throw new Error(invokeErr.message)
      if (data?.error) throw new Error(data.error)
      if (data?.draft) setReply(data.draft)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not draft — please try again.')
    } finally {
      setDrafting(false)
    }
  }

  async function sendReply(text: string): Promise<string | null> {
    if (!message) return 'No message selected.'
    const { data, error: invokeErr } = await supabase.functions.invoke('staff-reply-parent', {
      body: { message_id: message.id, body: text },
    })
    if (invokeErr) return 'Could not send — please try again.'
    if (data?.error) return data.error
    onSent()
    return null
  }

  return (
    <Dialog open={!!message} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>
        Reply to {message?.parent_name} <Typography component="span" color="text.secondary" sx={{ fontWeight: 400 }}>· re {message?.student_name}</Typography>
      </DialogTitle>
      <DialogContent>
        {message && (
          <Box sx={{ background: '#F4EFE6', borderRadius: 2, p: 1.5, mb: 2 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, display: 'block', mb: 0.5 }}>
              Parent's message
            </Typography>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.55 }}>{message.body}</Typography>
          </Box>
        )}
        {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}
        <PolishComposer
          value={reply}
          onChange={setReply}
          onSend={sendReply}
          placeholder="Write your reply, or let AI draft one to edit…"
          sendLabel="Send reply"
          rows={8}
          leftSlot={
            <Button
              size="small" variant="outlined"
              startIcon={drafting ? <CircularProgress size={14} /> : <AutoFixHighIcon fontSize="small" />}
              onClick={draft}
              disabled={drafting}
            >
              {drafting ? 'Drafting…' : 'Draft with AI'}
            </Button>
          }
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>Cancel</Button>
      </DialogActions>
    </Dialog>
  )
}
